/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { handleSettingsChange, otherReportCard } from '.';
import { UtrvStatus } from '../../constants/status';
import { useAppSelector } from '../../reducers';
import { isOrgManager } from '../../selectors/user';
import { CardsTotals, DownloadMultiScope, ScopeTotals, VisibilityStatus } from '../../types/download';
import { DownloadButton } from '../button/DownloadButton';
import { DownloadSettingModal, DownloadSettingPropType } from './DownloadSettingModal';
import SimpleTooltip from '../simple-tooltip';
import { BuySurveyButton } from './BuySurveyButton';
import { DownloadCsv, DownloadPptx, downloadReportHandler, DownloadSettingsType, DownloadSimple, DownloadXlsx } from './util/downloadReportHandler';
import { getDefaultDownloadScope } from '../report-output/outputs';
import { getGroup } from '@g17eco/core';
import { DownloadOthersSettingModal } from './DownloadOthersSettingModal';
import { Totals } from './partials/Totals';
import { SURVEY_PACKS, SURVEY_PACKS_SHORT_NAME, getSortedVersions } from '../../constants/standards-frameworks';
import { Scope } from '../../model/surveyData';

export interface DownloadCardItem {
  code: string;
  name: string;
  versionGroupCode?: string;
  hideXlsx?: boolean;
};

interface DownloadItem extends DownloadCardItem {
  src?: string;
  icon?: string;
  tooltip?: string;
  disabled?: boolean;
}

interface DownloadCardProps {
  downloadReport?: (code: string, downloadScope: DownloadMultiScope) => Promise<void>;
  downloadSimple?: DownloadSimple;
  downloadXlsx?: DownloadXlsx;
  downloadPptx?: DownloadPptx;
  downloadCsv?: DownloadCsv;
  item: DownloadItem;
  isDisabled?: boolean;
  noSurvey?: boolean;
  children?: JSX.Element;
  aside?: boolean;
  downloadButton?: JSX.Element;
  totals?: ScopeTotals;
  getTotals?: (type: string) => CardsTotals[typeof type];
  surveyScope?: Partial<Scope>;
}

const getAsideClassName = (aside: boolean) => `download-actions d-flex flex-column ${aside ? 'mt-3 py-0 ml-2 pl-5' : ''}`;

const DownloadCardFooter = (props: DownloadCardProps) => {
  const { aside = false, item, isDisabled } = props;

  const group = getGroup('frameworks', item.code) || getGroup('standards', item.code);
  const isPremium = Boolean(group?.tags?.length);
  const isManager = useAppSelector(isOrgManager);

  let disabledText = '';
  if (isDisabled || item.disabled) {
    if (isPremium) {
      disabledText = isManager
        ? 'To access this report, you need to buy the relevant survey pack first'
        : 'To access this survey pack, please contact your organisation\'s administrator and request they purchase this pack for the organisation.';
    }
  }

  return <aside className={`${getAsideClassName(aside)} text-label mt-1`}>{disabledText}</aside>;
};

const DownloadCard = (props: DownloadCardProps) => {
  const {
    aside = false,
    item,
    downloadReport,
    downloadButton,
    isDisabled,
    children,
    totals,
    getTotals,
    noSurvey,
    surveyScope,
    ...downloadHandlers
  } = props;

  const code = item.code; // Code means we can download reports
  const isOtherReport = code === otherReportCard.code;
  const renderDownload = downloadReport && (code || isOtherReport);
  const [openSettings, setOpenSettings] = useState(false);

  const handleCloseModal = () => setOpenSettings(false);
  const handleOpenSettings = (e: React.MouseEvent<Element, MouseEvent>) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.blur();
    }
    setOpenSettings(true);
  };

  const handleDownloadReport = () => {
    if (!downloadReport) {
      return;
    }
    const downloadScope = getDefaultDownloadScope([code], { ...downloadSettings });
    return downloadReport(code, downloadScope);
  }

  const [downloadSettings, setDownloadSettings] = useState<DownloadSettingsType>({
    selectedScopes: [],
    visibility: VisibilityStatus.ExcludeData,
    status: UtrvStatus.Verified
  });

  const downloadModalMinData = {
    title: item.name,
    code: item.code,
    isDisabled: isDisabled || noSurvey || item.disabled,
    handleClose: handleCloseModal,
    handleChange: (event: React.ChangeEvent<HTMLInputElement>) => handleSettingsChange(event, setDownloadSettings, downloadSettings),
    downloadDoc: () => handleDownloadReport(),
    onDownload: (type: string, config?: { packCode?: string; debug?: boolean }) =>
      downloadReportHandler({ type, code: config?.packCode || code, downloadSettings, ...downloadHandlers }),
    downloadSettings,
  };

  const renderDownloadModal = () => {
    const isInScope = (scopes: string[] | undefined, pack: string) => {
      return (scopes ?? []).includes(pack);
    }

    if (item.versionGroupCode) {
      const versions = getSortedVersions(item.versionGroupCode);
      const [latestVersion, prevVersion] = versions;

      const surveyScopes = surveyScope ? [...(surveyScope.standards ?? []), ...(surveyScope.frameworks ?? [])] : []

      const multiVersionModalProps = {
        ...downloadModalMinData,
        disabledOptions: [],
        getTotals,
        packTitle: 'Version',
        isOpen: Boolean(item.versionGroupCode) && openSettings,
        packOptions: SURVEY_PACKS_SHORT_NAME.filter((pack) => versions.map((i) => i.code).includes(pack.value)),
        defaultPackCode:
          !isInScope(surveyScopes, latestVersion.code) && isInScope(surveyScopes, prevVersion.code)
            ? prevVersion.code
            : latestVersion.code,
      };

      return <DownloadOthersSettingModal {...multiVersionModalProps} />
    }

    if (isOtherReport) {
      const otherModalProps = {
        ...downloadModalMinData,
        isOpen: isOtherReport && openSettings,
        getTotals,
        packOptions: SURVEY_PACKS
      }
      return <DownloadOthersSettingModal {...otherModalProps} />
    }

    const dowloadModalProps: DownloadSettingPropType = {
      ...downloadModalMinData,
      isOpen: openSettings,
      totals,
    };
    return <DownloadSettingModal {...dowloadModalProps} />
  }

  return (
    <div className={`download-card ${aside ? 'd-flex flex-wrap align-self-baseline flex-fill' : ''}`}>
      <section className='download-details d-flex flex-column'>
        <div className='download-details-internal'>
          <div className='mb-auto text-center'>
            {item.src ? <img src={item.src} alt={item.name} width='70px' height='70px' /> : null}
            {item.icon ? <i className={`mt-1 fa ${item.icon} fa-5x text-ThemeAccentExtradark`} /> : null}
            <div className='strong mt-4 text-center download-item-name'>{item.name}</div>
          </div>
          <div className='mt-5'>
            {downloadButton ? downloadButton : null}
            {renderDownload ? (
              <SimpleTooltip text={item.tooltip}>
                <DownloadButton
                  className='doc-btn'
                  color='secondary'
                  outline={true}
                  key={code}
                  onClick={handleOpenSettings}
                  disabled={isDisabled || item.disabled || totals?.total === 0}
                >
                  Download report
                  <i className='fas fa-file-download ml-2' />
                </DownloadButton>
              </SimpleTooltip>
            ) : (
              <BuySurveyButton scopeTag={code} tooltip={item.tooltip} />
            )}
            {children}
          </div>
        </div>
        <Totals totals={totals} />
      </section>
      <DownloadCardFooter {...props} />
      {renderDownloadModal()}
    </div>
  );
};

export default DownloadCard;
